import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {Handler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {buildChannelSettingsPath, ChannelErrorsEnum} from '../../../../helpers/models/channelHelpers';
import {
  isDraftInNewConversation,
  isForwardingToTeammate,
  isMassSend,
  SendDraftErrorsEnum,
} from '../../../../helpers/models/messageHelpers';
import {ResolveDispatchProps} from '../../../../helpers/reduxHelpers';
import {
  AppParametricSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
  ResolveStateProps,
} from '../../../../helpers/reselectHelpers';
import {ChannelModel} from '../../../../models/api/channelModel';
import {MessageErrorModel} from '../../../../models/api/messageErrorModel';
import {DraftMessageModel} from '../../../../models/api/messageModel';
import {AppState} from '../../../../store/appState';
import {makeGetChannelInbox} from '../../../../store/data/channels/channelsSelectors';
import {getContacts} from '../../../../store/data/dataSelectors';
import {getMentionableEmails} from '../../../../store/data/teammates/mentionTeammatesSelectors';
import {
  getComposerReplacePilledVariablesInMessageTemplates,
  getHasNewInboxSettingsDetails,
  getHasResolveDrafts,
} from '../../../../store/featureFlags/featureFlagsSelectors';
import {navigateToLinkAction} from '../../../../store/stateless/navigationActions';
import {
  ComposerBanner,
  ComposerBannerErrorsEnum,
  ComposerBannerLevelsEnum,
  MessageTipsEnum,
} from './composerBanner';

/*
 * Props.
 */

interface ComposerBannerContainerProps {
  message: DraftMessageModel;
  validationErrors: ReadonlyArray<ComposerBannerErrorsEnum>;
  hasRawVariables?: boolean;
  saveError?: MessageErrorModel;
  attemptedSendDraftAttachments: boolean;
  dismissAttemptedSendDraftAttachments: Handler;
  conflictLinkAction?: Handler;
  channel: ChannelModel;
}

/*
 * Selectors.
 */

type GetValidationErrors = AppParametricSelector<any, ReadonlyArray<ComposerBannerErrorsEnum>>;

function getMessage(_state: AppState, props: ComposerBannerContainerProps) {
  return props.message;
}

function getValidationErrors(_state: AppState, props: ComposerBannerContainerProps) {
  return props.validationErrors;
}

function getHasRawVariables(_state: AppState, props: ComposerBannerContainerProps) {
  return props.hasRawVariables;
}

function makeGetBannerValidationErrors(getValidationErrorsSelector: GetValidationErrors) {
  return createSelector(getValidationErrorsSelector, (validationErrors) =>
    validationErrors.filter((error) => error !== SendDraftErrorsEnum.INCOMPLETE),
  );
}

function getSaveError(_state: AppState, props: ComposerBannerContainerProps) {
  return props.saveError;
}

function getAttemptedSendDraftAttachments(_state: AppState, props: ComposerBannerContainerProps) {
  return props.attemptedSendDraftAttachments;
}

function getDismissAttemptedSendDraftAttachments(_state: AppState, props: ComposerBannerContainerProps) {
  return props.dismissAttemptedSendDraftAttachments;
}

function getConflictLinkAction(_state: AppState, props: ComposerBannerContainerProps) {
  return props.conflictLinkAction;
}

function makeGetBannerOptions() {
  return createSelector(
    [
      getMessage,
      makeGetBannerValidationErrors(getValidationErrors),
      getHasRawVariables,
      getSaveError,
      getMentionableEmails,
      getContacts,
      getConflictLinkAction,
      getAttemptedSendDraftAttachments,
      getDismissAttemptedSendDraftAttachments,
      getChannelErrorType,
      getComposerReplacePilledVariablesInMessageTemplates,
    ],
    (
      message,
      bannerValidationErrors,
      hasRawVariables,
      saveError,
      mentionableEmails,
      contacts,
      conflictLinkAction,
      attemptedSendDraftAttachments,
      dismissAttemptedSendDraftAttachments,
      channelErrorType,
      hasRawVariablesWarningFeatureFlag,
    ) => {
      if (bannerValidationErrors.length > 0) {
        return {type: bannerValidationErrors[0], level: ComposerBannerLevelsEnum.WARNING};
      }

      if (saveError) {
        return {
          type: saveError.type,
          description: saveError.description,
          level: ComposerBannerLevelsEnum.WARNING,
        };
      }

      if (conflictLinkAction) {
        return {
          type: MessageTipsEnum.CONFLICTING_COPY,
          level: ComposerBannerLevelsEnum.INFORMATION,
          linkAction: conflictLinkAction,
        };
      }

      if (isMassSend(message)) {
        return {type: MessageTipsEnum.MASS_SEND, level: ComposerBannerLevelsEnum.INFORMATION};
      }

      if (
        isForwardingToTeammate(contacts, mentionableEmails, message) &&
        !isDraftInNewConversation(message)
      ) {
        return {type: MessageTipsEnum.TEAMMATE_FORWARD, level: ComposerBannerLevelsEnum.INFORMATION};
      }

      if (attemptedSendDraftAttachments) {
        return {
          type: MessageTipsEnum.HAS_DRAFT_ATTACHMENTS,
          level: ComposerBannerLevelsEnum.WARNING,
          dismissAction: dismissAttemptedSendDraftAttachments,
        };
      }

      if (channelErrorType === ChannelErrorsEnum.MISSING_O365_SEND_PERMISSIONS) {
        return {
          type: ChannelErrorsEnum.MISSING_O365_SEND_PERMISSIONS,
          level: ComposerBannerLevelsEnum.WARNING,
        };
      }

      if (hasRawVariables && hasRawVariablesWarningFeatureFlag) {
        return {
          type: MessageTipsEnum.HAS_RAW_VARIABLES,
          level: ComposerBannerLevelsEnum.WARNING,
        };
      }

      return undefined;
    },
  );
}

// Selectors

const getChannel = (_state: AppState, props: ComposerBannerContainerProps) => props.channel;

const getInbox = makeGetChannelInbox(getChannel);

const getChannelErrorType = createSelector(getChannel, (channel) => channel?.error?.type);

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<ComposerBannerContainerProps>()(() => ({
  options: makeGetBannerOptions(),
  inbox: getInbox,
  hasNewInboxSettings: getHasNewInboxSettingsDetails,
  hasResolveDrafts: getHasResolveDrafts,
}));

const mapDispatchToProps = makeMapDispatchToProps<ComposerBannerContainerProps>()(() => ({
  onNavigateToLink: navigateToLinkAction,
}));

function mergeProps(
  stateProps: ResolveStateProps<typeof mapStateToProps>,
  dispatchProps: ResolveDispatchProps<typeof mapDispatchToProps>,
  ownProps: ComposerBannerContainerProps,
) {
  const {channel} = ownProps;
  const {inbox, hasNewInboxSettings} = stateProps;
  const {onNavigateToLink} = dispatchProps;

  const onOpenSettings = () => {
    if (!inbox) {
      return;
    }

    onNavigateToLink(buildChannelSettingsPath(hasNewInboxSettings, inbox, channel));
  };

  return {
    ...ownProps,
    ...stateProps,
    ...dispatchProps,
    onOpenSettings,
  };
}

export const ComposerBannerContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps,
)(ComposerBanner);
